import {
	OrderTypes,
	useGetAllProductsQuery,
	type GetAllProductInput,
	type GetAllProductsQuery,
} from '@codezo/graphql';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductCard } from '../ProductCard';
import { Button } from '../ui/button';

type Product = GetAllProductsQuery['getAllProduct']['data'][number];

/** Two rows' worth of the widest layout, so the next page is usually loaded
 * before the chevron reaches the end of the current one. */
const PAGE_SIZE = 10;

/**
 * A client's or a brand's products as one scrollable row of the products page's
 * own cards, newest first.
 *
 * It pages in as you scroll rather than stopping at a fixed handful, so every
 * product is reachable from here; "See all" stays for searching and bulk work.
 * The section head belongs to the caller — the client and brand pages each use
 * their own package's — so it is handed the total through `head`.
 */
export function ProductCarousel({
	filter,
	head,
	empty,
	onTotal,
}: {
	/** What to list — `organizationId` or `brandId`. Pagination and order are ours. */
	filter: Omit<GetAllProductInput, 'pagination' | 'order'>;
	head: (total: number) => ReactNode;
	empty: ReactNode;
	/** The full count, for a caller that shows it elsewhere too (an index rail). */
	onTotal?: (total: number) => void;
}) {
	const navigate = useNavigate();
	const scrollRef = useRef<HTMLDivElement>(null);
	const [page, setPage] = useState(1);
	const [items, setItems] = useState<Product[]>([]);
	/* Kept, not read off `data`: while the next page loads `data` is empty, and
	   reading the total from it would disable Next until the page arrives. */
	const [total, setTotal] = useState<number | null>(null);
	const [canPrev, setCanPrev] = useState(false);
	const [canNext, setCanNext] = useState(false);

	/* A new filter is a new list: start over rather than appending to the old one. */
	const filterKey = JSON.stringify(filter);
	useEffect(() => {
		setPage(1);
		setItems([]);
		setTotal(null);
	}, [filterKey]);

	const { data, loading } = useGetAllProductsQuery({
		variables: {
			input: {
				...filter,
				pagination: { limit: PAGE_SIZE, currentPage: page },
				order: { field: 'createdAt', direction: OrderTypes.Desc },
			},
		},
		fetchPolicy: 'no-cache',
	});

	useEffect(() => {
		const fetched = data?.getAllProduct?.data;
		if (!fetched) return;
		const count = data.getAllProduct.pageInfo?.totalCount;
		if (count != null) setTotal(count);
		setItems((prev) => {
			const seen = new Set(prev.map((p) => p.id));
			return [...prev, ...fetched.filter((p) => !seen.has(p.id))];
		});
	}, [data]);

	useEffect(() => {
		if (total != null) onTotal?.(total);
	}, [total, onTotal]);

	const hasMore = items.length < (total ?? 0);

	/* The chevrons follow the scroll position, not a page index, so a trackpad
	   swipe and a click leave them in the same state. Near the end, the next
	   page is fetched so the row keeps going. */
	const sync = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
		setCanPrev(el.scrollLeft > 4);
		setCanNext(!atEnd || hasMore);
		if (hasMore && !loading && el.scrollLeft + el.clientWidth * 2 >= el.scrollWidth) {
			setPage((p) => p + 1);
		}
	}, [hasMore, loading]);

	useEffect(() => {
		sync();
		const el = scrollRef.current;
		if (!el) return;
		const observer = new ResizeObserver(sync);
		observer.observe(el);
		return () => observer.disconnect();
	}, [sync, items.length]);

	const scrollBy = (direction: 1 | -1) => {
		const el = scrollRef.current;
		if (!el) return;
		el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
	};

	if (!loading && items.length === 0) {
		return (
			<>
				{head(0)}
				{empty}
			</>
		);
	}

	return (
		<>
			{head(total ?? items.length)}
			<div
				ref={scrollRef}
				onScroll={sync}
				className="grid snap-x snap-mandatory auto-cols-[calc((100%-0.875rem)/2)] grid-flow-col gap-3.5 overflow-x-auto [scrollbar-width:none] sm:auto-cols-[calc((100%-1.75rem)/3)] xl:auto-cols-[calc((100%-3.5rem)/5)] [&::-webkit-scrollbar]:hidden"
			>
				{items.map((product) => (
					<div key={product.id} className="min-w-0 snap-start">
						<ProductCard
							name={product.name}
							sku={product.sku}
							images={product.images}
							tags={product.tags}
							showOwner={false}
							onClick={() => navigate(`/products/${product.id}`)}
						/>
					</div>
				))}
			</div>
			{(canPrev || canNext) && (
				<div className="mt-3 flex justify-end gap-1.5">
					<Button
						variant="outline"
						size="icon-sm"
						aria-label="Previous products"
						disabled={!canPrev}
						onClick={() => scrollBy(-1)}
					>
						<ChevronLeft size={14} />
					</Button>
					<Button
						variant="outline"
						size="icon-sm"
						aria-label="Next products"
						disabled={!canNext}
						onClick={() => scrollBy(1)}
					>
						<ChevronRight size={14} />
					</Button>
				</div>
			)}
		</>
	);
}
